import { z } from 'zod';
import { signupInput } from './schemas.js';

/**
 * A team's join link: one per team, shared in a group chat or as a QR code. Anyone with it can
 * ask to join as a player or parent; a coach approves each request. Turn it off or reset it
 * (the old link stops working) on the People tab.
 */

/** Same alphabet as claim codes: no 0/O, 1/I/L. */
export const JOIN_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
export const joinCode = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[2-9A-HJKMNP-Z]{10}$/, 'That join link isn’t right.');

export interface JoinLink {
  /** Null until a coach makes one. */
  code: string | null;
  enabled: boolean;
  /** Full link to share, e.g. https://app.hoopify.com.au/join/K7QM4XPD2H */
  url: string | null;
}

export const joinLinkInput = z.object({ enabled: z.boolean() });

/** What the public join page shows. */
export interface JoinPreview {
  teamName: string;
  ageGroup: string | null;
  /** New accounts need email (to confirm the address). */
  signupsOpen: boolean;
}

const joinFields = {
  role: z.enum(['player', 'parent'], { errorMap: () => ({ message: 'Choose whether you’re a player or a parent.' }) }),
  playerName: z.string().trim().min(1, 'Enter the player’s name.').max(80, 'Keep the name under 80 characters.'),
};
/** Signed in already: just who they are on the team. */
export const joinRequestInput = z.object(joinFields);
export type JoinRequestInput = z.infer<typeof joinRequestInput>;
/** New to Hoopify: an account and the request together. */
export const joinRegisterInput = signupInput.extend(joinFields);
export type JoinRegisterInput = z.infer<typeof joinRegisterInput>;

/** A request waiting for a coach (only once their email is confirmed). */
export interface JoinRequest {
  id: string;
  name: string;
  email: string;
  role: 'player' | 'parent';
  playerName: string;
  requestedAt: string;
}

/** Your own requests, for the Teams page. */
export interface MyJoinRequest {
  teamName: string;
  status: 'pending' | 'declined';
  /** False until you confirm your email: the coach can't see it yet. */
  emailConfirmed: boolean;
  requestedAt: string;
}

export const approveJoinInput = z.object({
  link: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('none') }),
    z.object({ kind: z.literal('player'), playerId: z.string().uuid() }),
    z.object({ kind: z.literal('new') }),
  ]),
});
export type ApproveJoinInput = z.infer<typeof approveJoinInput>;

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
/**
 * The roster player a typed name most likely means: the same name, then the same first name and
 * last initial, then the same first name if only one player has it. Null if unsure.
 */
export function suggestRosterMatch<T extends { id: string; name: string }>(typed: string, roster: T[]): T | null {
  const t = norm(typed);
  if (!t) return null;
  const exact = roster.filter((p) => norm(p.name) === t);
  if (exact.length === 1) return exact[0]!;
  const [first, ...rest] = t.split(' ');
  const lastInitial = rest.at(-1)?.[0];
  if (lastInitial) {
    const close = roster.filter((p) => {
      const [f, ...r] = norm(p.name).split(' ');
      return f === first && r.at(-1)?.[0] === lastInitial;
    });
    if (close.length === 1) return close[0]!;
  }
  const sameFirst = roster.filter((p) => norm(p.name).split(' ')[0] === first);
  return sameFirst.length === 1 ? sameFirst[0]! : null;
}
